import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrailers } from '../trailerData';
import { useBusinessSettings } from '../BusinessSettingsContext';
import SEO, { LocalBusinessSEO } from '../components/SEO';
import './HomePage.css';

// Helper to get industry-specific terminology
function getTerminology(industryTypes) {
  if (!industryTypes || industryTypes.length === 0) return { singular: 'rental', plural: 'rentals', unit: 'trailer' };

  if (industryTypes.includes('rv')) return { singular: 'RV', plural: 'RVs', unit: 'RV' };
  if (industryTypes.includes('travel_trailer')) return { singular: 'trailer', plural: 'trailers', unit: 'trailer' };
  if (industryTypes.includes('dumpster')) return { singular: 'dumpster', plural: 'dumpsters', unit: 'dumpster' };
  if (industryTypes.includes('portable_restroom')) return { singular: 'restroom', plural: 'restrooms', unit: 'unit' };

  return { singular: 'rental', plural: 'rentals', unit: 'unit' };
}

function HomePage() {
  const { trailers, loading } = useTrailers();
  const { settings } = useBusinessSettings();
  const navigate = useNavigate();

  const terms = getTerminology(settings.industry_types);
  const heroTagline = settings.tagline || 'Your Adventure Starts Here';

  return (
    <div className="home-page">
      <SEO
        title={`${terms.singular.toUpperCase()} Rentals`}
        description={`${settings.business_name} - ${heroTagline}. Browse our selection of quality ${terms.plural} and book your next adventure today!`}
        url="/"
      />
      <LocalBusinessSEO />

      <HeroSection settings={settings} terms={terms} />
      <AllTrailers trailers={trailers} loading={loading} terms={terms} navigate={navigate} />
      <CTASection settings={settings} terms={terms} />
    </div>
  );
}

// Hero Section
function HeroSection({ settings, terms }) {
  return (
    <section className="hero-section">
      <div className="hero-overlay"></div>
      <div className="hero-content">
        <h1>{settings.tagline || 'Your Adventure Starts Here'}</h1>
        <p className="hero-subtitle">
          {settings.contact_address
            ? `Quality ${terms.plural} in ${settings.contact_address}`
            : `Quality ${terms.plural} for your next adventure`}
        </p>
        <button
          className="hero-btn"
          onClick={() => document.getElementById('all-trailers')?.scrollIntoView({ behavior: 'smooth' })}
        >
          Browse {terms.plural}
        </button>
      </div>
    </section>
  );
}

// All Trailers Grid
function AllTrailers({ trailers, loading, terms, navigate }) {
  if (loading) {
    return (
      <section className="all-trailers-section" id="all-trailers">
        <div className="section-header">
          <h2>Available {terms.plural}</h2>
        </div>
        <div className="loading-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="trailer-card-skeleton">
              <div className="skeleton-image"></div>
              <div className="skeleton-content">
                <div className="skeleton-title"></div>
                <div className="skeleton-text"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="all-trailers-section" id="all-trailers">
      <div className="section-header">
        <h2>Available {terms.plural}</h2>
        <p>Browse our complete selection</p>
      </div>

      {trailers.length === 0 ? (
        <p className="no-trailers">No {terms.plural} available at the moment. Please check back soon!</p>
      ) : (
        <div className="trailer-grid">
          {trailers.map(trailer => {
            const isCargo = trailer.trailer_type === 'cargo';
            // Keep in sync with CARGO_SUBTYPE_LABEL in TrailersPage.js
            // and VALID_CARGO_SUBTYPES in backend/routes/trailers.js.
            const cargoLabel = isCargo
              ? ({ utility: 'Utility', enclosed: 'Enclosed', carhauler: 'Car Hauler', other: 'Cargo' }[trailer.cargo_subtype] || 'Cargo')
              : null;
            return (
            <div
              key={trailer.id}
              className="trailer-card"
              onClick={() => navigate(`/trailer/${trailer.id}`)}
            >
              <div className="card-image">
                <img
                  src={trailer.images[0]}
                  alt={trailer.name}
                  loading="lazy"
                />
                {trailer.status === 'available' && (
                  <div className="availability-badge">Available</div>
                )}
              </div>
              <div className="card-content">
                <h3>{trailer.name}</h3>
                <p className="card-location">{trailer.location}</p>
                <div className="card-specs">
                  {isCargo ? <span>{cargoLabel} trailer</span> : <span>Sleeps {trailer.sleeps}</span>}
                  {trailer.length && <span>{trailer.length}</span>}
                </div>
                {Array.isArray(trailer.features) && trailer.features.length > 0 && (
                  <div className="card-features">
                    {trailer.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="feature-tag">{feature}</span>
                    ))}
                  </div>
                )}
                <div className="card-footer">
                  <div className="card-price">
                    <span className="amount">${trailer.price_per_night}</span>
                    <span className="period">/{isCargo ? 'day' : 'night'}</span>
                  </div>
                  <button className="view-btn">View Details</button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// CTA Section
function CTASection({ settings, terms }) {
  const navigate = useNavigate();

  return (
    <section className="cta-section">
      <div className="cta-content">
        <h2>Ready for Your Next Adventure?</h2>
        <p>Have questions? We're here to help.</p>
        <div className="cta-buttons">
          <button
            className="cta-primary"
            onClick={() => navigate('/contact')}
          >
            Contact Us
          </button>
        </div>
        {settings.contact_phone && (
          <p className="cta-phone">
            Or call us: <a href={`tel:${settings.contact_phone}`}>{settings.contact_phone}</a>
          </p>
        )}
      </div>
    </section>
  );
}

export default HomePage;
